import { Box } from '@mui/material';
import type { ContractType } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import type { ContractStaffing } from './contractStaffing';
import { contractPlanReading } from './planReading';

import type { BudgetMetric } from '@/components/financials/metricReading';
import { formatFactorAs } from '@/components/financials/metricReading';
import { NoticeChip, SectionHeading } from '@/components/ui';
import { formatAmount } from '@/lib';

/**
 * The statements at the bottom of the task panel's Contract insights
 * (2026-11-02, the designer's ask): how the current plan compares against the
 * contract, read off the KPI boxes above — the forecasted factor is what the
 * allocated cost yields on the Own Work amount budget (align the target to it,
 * or bring the cost to meet the target); the forecasted amount is the budget
 * that holds the target at that cost (align the budget to it). Two sentences,
 * each naming the move and its figure; one when the plan is on target. See
 * `planReading.ts` for the figures and when there is nothing to say.
 *
 * Form: recommendations to the user, so a section of its own under Contract
 * insights (3 Nov, Mette's note) — the panel's section header, then one
 * `NoticeChip` per statement, as Financials shows its CALC-14 diff: warning
 * (yellow, ⓘ) for advice to act on, success (green, ✓) when the plan is on
 * target. The sunken panel it sat in until then read as one more insight.
 * Without an Own Work amount only the budget sentence shows — what the
 * allocations call for. Nothing on a rate-card contract or without cost: the
 * boxes above already hint why.
 *
 * A parent task reads every contract its subtasks map to (3 Nov, Mette), so
 * the section takes one group per contract and names each when there is more
 * than one; a leaf passes its one contract.
 */
export type ContractPlanReadingGroup = {
  /** The contract's id: the group's key. */
  key: string;
  /** The contract's name, shown over its statements when there are several groups. */
  label: string;
  staffing: ContractStaffing;
  contractType: ContractType;
  /** The target factor set on the contract; null when the task maps none. */
  ownFactor: number | null;
};

export type ContractPlanReadingProps = {
  groups: ContractPlanReadingGroup[];
  metric: BudgetMetric;
};

type Statement = { key: string; text: string };

export const ContractPlanReading = ({ groups, metric }: ContractPlanReadingProps) => {
  const { t } = useTranslation('pmPlanningPanel');

  const read = ({ staffing, contractType, ownFactor }: ContractPlanReadingGroup) => {
    const reading = contractPlanReading(staffing, contractType, ownFactor);
    if (!reading) return null;
    const target = formatFactorAs(metric, reading.target);
    const statements =
      reading.kind === 'noBudget'
        ? [
            {
              key: 'budget',
              text: t('planReading.NoBudget', {
                target,
                forecast: formatAmount(reading.budget.forecast),
              }),
            },
          ]
        : reading.kind === 'onTarget'
          ? [
              {
                key: 'on-target',
                text: t('planReading.OnTarget', { target, own: formatAmount(reading.own) }),
              },
            ]
          : [
              {
                key: 'factor',
                text: t(
                  reading.factor.direction === 'below'
                    ? 'planReading.FactorBelow'
                    : 'planReading.FactorAbove',
                  {
                    forecast: formatFactorAs(metric, reading.factor.forecast),
                    own: formatAmount(reading.own),
                    cost: formatAmount(reading.factor.costAtTarget),
                    target,
                  },
                ),
              },
              {
                key: 'budget',
                text: t(
                  reading.budget.direction === 'below'
                    ? 'planReading.BudgetBelow'
                    : 'planReading.BudgetAbove',
                  {
                    target,
                    forecast: formatAmount(reading.budget.forecast),
                    delta: formatAmount(reading.budget.delta),
                  },
                ),
              },
            ];
    return { kind: reading.kind, statements: statements as Statement[] };
  };

  const readings = groups.flatMap((group) => {
    const reading = read(group);
    return reading ? [{ ...group, ...reading }] : [];
  });
  if (readings.length === 0) return null;
  const named = readings.length > 1;

  return (
    <Box
      component="section"
      data-automation-id="task-contract-plan-reading"
      data-kind={named ? undefined : readings[0]!.kind}
      sx={{ mt: 6 }}
    >
      <Box sx={{ mb: 2 }}>
        <SectionHeading icon="lightbulb" component="h3">
          {t('planReading.Title')}
        </SectionHeading>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {readings.map((reading) => (
          <Box
            key={reading.key}
            data-automation-id="task-contract-plan-reading-group"
            data-contract-id={reading.key}
            data-kind={reading.kind}
          >
            {named ? (
              <Box sx={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-1)', mb: 1 }}>
                {reading.label}
              </Box>
            ) : null}
            <Box
              component="ul"
              sx={{
                m: 0,
                p: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {reading.statements.map((statement) => (
                <Box key={statement.key} component="li">
                  <NoticeChip
                    tone={reading.kind === 'onTarget' ? 'success' : 'warning'}
                    icon={reading.kind === 'onTarget' ? 'check_circle' : 'info'}
                    automationId={`task-contract-plan-reading-${statement.key}`}
                  >
                    {statement.text}
                  </NoticeChip>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
